import { fmt } from '../../lib/format';

export interface BarDatum {
  label: string;
  value: number | null;
}

/** A thin teal bar per row with the value beside it; labels wrap rather than truncate. */
export function BarRows({
  data,
  labelWidth = 210,
  track = 10,
}: {
  data: BarDatum[];
  labelWidth?: number;
  track?: number;
}) {
  const max = Math.max(...data.map((d) => d.value ?? 0), 1);

  return (
    <div>
      {data.map((d) => (
        <div
          key={d.label}
          className="grid items-center gap-4 py-[9px] border-b border-line max-md:!grid-cols-[1fr_64px]"
          style={{ gridTemplateColumns: `${labelWidth}px 1fr 78px` }}
        >
          <span className="text-[15px] leading-[22px] text-ink">{d.label}</span>
          <div className="max-md:col-span-2 max-md:order-last" style={{ height: track }}>
            <div
              className="h-full rounded-sm bg-accent"
              style={{ width: `${Math.max(((d.value ?? 0) / max) * 100, 0.6)}%` }}
            />
          </div>
          <span className={`tabular text-right text-[15px] ${d.value === null ? 'text-ink-3' : 'text-ink'}`}>
            {d.value === null ? '–' : fmt(d.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function ColumnChart({
  data,
  currentIndex,
  height = 230,
}: {
  data: { label: string; value: number }[];
  currentIndex?: number;
  height?: number;
}) {
  const max = Math.max(...data.map((d) => d.value), 1);

  return (
    <div>
      <div className="grid gap-[14px] items-end" style={{ gridTemplateColumns: `repeat(${data.length}, 1fr)`, height }}>
        {data.map((d, i) => (
          <div key={d.label} className="flex flex-col justify-end h-full">
            <span className={`tabular text-small mb-1 ${i === currentIndex ? 'text-ink font-semibold' : 'text-ink-2'}`}>
              {fmt(d.value)}
            </span>
            <div
              className={`rounded-t-sm ${i === currentIndex ? 'bg-accent' : 'bg-line-strong'}`}
              style={{ height: `${Math.max((d.value / max) * 100, 2)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="border-t border-line-strong mt-2 pt-2">
        <div className="grid gap-[14px]" style={{ gridTemplateColumns: `repeat(${data.length}, 1fr)` }}>
          {data.map((d) => (
            <span key={d.label} className="text-small text-ink-3">
              {d.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
